import React, { useRef, useMemo, useState, useEffect } from 'react';
import { buildEngine } from '../../engine/Engine';
import { CommunitiesSlider } from '../../components/CommunitiesSlider';
import LocationSelector from '../LocationSelector';
import SearchContext from '../../state/context';
import { buildResultList, buildResultsPerPage } from '@coveo/headless';
import {
  getLocationCookie,
  sendCoveoGeolocationInterfaceLoad,
  sendCoveoGeolocationQuery,
  getCoveoFindACommunityFieldsToInclude,
  sortResultsByDistance
} from '../../helpers';
import useEventBus, { dispatch } from '../../../hooks/useEventBus';

const NearbyCommunitiesRow = ({
  withLocationSelector,
  showPagination = null,
  locationCookieName,
  coveoOrganizationId,
  coveoAccessToken,
  coveoSearchHub,
  urlUsa = null,
  urlCanada = null,
  ctaNearbyLink = null,
  ctaNearbyLinkLabel = null,
  headingSingular,
  headingPlural,
  coveoSearchRadiusInMiles,
  coveoContextLanguage,
  coveoSource,
  coveoPipeline,
  featuredItems,
  zipLink,
  searchWithInfoRedirect = null,
  searchNoInfoRedirect = null,
  countryOptions = [],
  careTypeOptions = [],
  distanceOptions = [],
  careTypePlaceholder,
  distancePlaceholder,
  searchButtonLabel,
  searchBoxPlaceholder = 'Enter a zip code',
  featuredItemsCta = 'Learn More',
  coveoAutocompleteFields,
  module
}) => {
  const [numberOfCommunities, setNumberOfCommunities] = useState(null);
  const [waitingForGeolocation, setWaitingForGeolocation] = useState(true);
  const [geolocationData, setGeolocationData] = useState(null);
  const [displayedResults, setDisplayedResults] = useState([]);

  const isSearching = useRef(false);
  const searchSource = useRef(
    countryOptions && countryOptions.length > 0
      ? JSON.parse(countryOptions).find(
          option => option.defaultValue === 'true'
        ).source
      : coveoSource
  );
  const searchDistance = useRef(null);
  const searchCareType = useRef(null);
  const firstLoad = useRef(true);

  const featuredItemsResults = useMemo(
    () => (featuredItems ? JSON.parse(featuredItems) : []),
    []
  );

  const engine = useMemo(() => {
    return buildEngine({
      organizationId: coveoOrganizationId,
      accessToken: coveoAccessToken,
      searchHub: coveoSearchHub,
      pipeline: coveoPipeline
    });
  }, []);

  const resultsListController = useMemo(
    () =>
      buildResultList(engine, {
        options: {
          fieldsToInclude: [
            ...getCoveoFindACommunityFieldsToInclude(),
            'ssl_mainimagedesktop_405x548'
          ]
        }
      }),
    []
  );

  useEffect(() => {
    resultsListController.subscribe(() => {
      const originalResults = [...resultsListController.state.results];
      const sortedResults = sortResultsByDistance(originalResults);

      setDisplayedResults(sortedResults);

      if (resultsListController.state.results.length > 0) {
        setNumberOfCommunities(resultsListController.state.results.length);
      }

      const parentContainer = document.querySelector(
        '.nearby-communities-row-headless'
      );
      if (parentContainer) {
        if (resultsListController.state.results.length === 0) {
          parentContainer.classList.add(
            'nearby-communities-row-headless--no-results'
          );
        } else {
          parentContainer.classList.remove(
            'nearby-communities-row-headless--no-results'
          );
        }
      }
    });
  }, [geolocationData]);

  useEventBus(
    'geolocator-loaded',
    () => {
      let cookieData = getLocationCookie(locationCookieName);
      if (cookieData) {
        setGeolocationData(cookieData);

        let loaded = !firstLoad.current;

        sendCoveoGeolocationInterfaceLoad({
          engine: engine,
          latitude: cookieData.Latitude,
          longitude: cookieData.Longitude,
          source: searchSource.current,
          radiusInMiles: parseFloat(coveoSearchRadiusInMiles),
          language: coveoContextLanguage,
          pipeline: coveoPipeline,
          previousLoad: loaded
        });
        firstLoad.current = false;
      }
      setWaitingForGeolocation(false);
    },
    []
  );

  const getHeadingString = () => {
    if (!headingSingular || !headingPlural) return null;
    if (numberOfCommunities === 1)
      return `${headingSingular.replace(
        '{NumberOfCommunities}',
        numberOfCommunities
      )} `;
    if (numberOfCommunities > 1)
      return `${headingPlural.replace(
        '{NumberOfCommunities}',
        numberOfCommunities
      )} `;
    return '';
  };

  const sendQuery = () => {
    let cookieData = getLocationCookie(locationCookieName);
    if (cookieData) {
      setGeolocationData(cookieData);
      sendCoveoGeolocationQuery({
        engine: engine,
        latitude: cookieData.Latitude,
        longitude: cookieData.Longitude,
        source: searchSource.current,
        radiusInMiles: searchDistance.current
          ? searchDistance.current.value
          : parseFloat(coveoSearchRadiusInMiles),
        careType: searchCareType.current ? searchCareType.current.value : null,
        state: cookieData.State,
        language: coveoContextLanguage,
        pipeline: coveoPipeline
      });
    }
    setWaitingForGeolocation(false);
  };

  useEventBus(
    'geolocation-received',
    ({ payload }) => {
      if (searchNoInfoRedirect !== null && searchWithInfoRedirect !== null)
        return;
      if (payload.Module === module) {
        sendQuery();
        setWaitingForGeolocation(false);
      } else {
        return;
      }
    },
    []
  );

  const handleLocationSelectorSubmit = (
    searchInputValue,
    selectedCountry,
    selectedCareType,
    selectedDistance
  ) => {
    isSearching.current = true;
    if (selectedCareType) searchCareType.current = selectedCareType;
    if (selectedDistance) searchDistance.current = selectedDistance;
    dispatch({
      type: 'get-zip-geolocation',
      payload: {
        value: searchInputValue,
        country: `${selectedCountry.value === 'us' ? 'USA' : 'CANADA'}`,
        moduleName: module,
        firstSearch: true
      }
    });
    setWaitingForGeolocation(true);
  };

  const resultsPerPageController = useMemo(
    () =>
      buildResultsPerPage(engine, {
        initialState: {
          numberOfResults: withLocationSelector ? 100 : 10
        }
      }),
    []
  );

  return (
    <>
      <SearchContext.Provider
        value={{
          engine: engine
        }}
      >
        <div
          className={`${
            resultsListController.state.results.length > 0
              ? 'nearby-communities-row-headless'
              : ''
          }`}
        >
          <div className="container">
            {!headingSingular || !headingPlural ? (
              ''
            ) : (
              <div className="nearby-communities-row-headless__heading">
                <div className="rich-text-row rtf container">
                  {numberOfCommunities && (
                    <h2>
                      {getHeadingString()}
                      {geolocationData && geolocationData.LastSearchValue && (
                        <a href={zipLink}>{geolocationData.LastSearchValue}</a>
                      )}
                    </h2>
                  )}
                </div>
              </div>
            )}
            {withLocationSelector && (
              <div className="location-selector-headless location-selector-headless--no-padding">
                <LocationSelector
                  module={module}
                  urlUsa={urlUsa}
                  urlCanada={urlCanada}
                  isAutocomplete={true}
                  removeLastSearchValue={false}
                  locationCookieName={locationCookieName}
                  coveoEngine={engine}
                  countryOptions={countryOptions}
                  coveoContextLanguage={coveoContextLanguage}
                  careTypePlaceholder={careTypePlaceholder}
                  careTypeOptions={careTypeOptions}
                  distancePlaceholder={distancePlaceholder}
                  distanceOptions={distanceOptions}
                  searchButtonLabel={searchButtonLabel}
                  searchBoxPlaceholder={searchBoxPlaceholder}
                  searchWithInfoRedirect={searchWithInfoRedirect}
                  searchNoInfoRedirect={searchNoInfoRedirect}
                  allowEmptySearch={true}
                  externalSubmitHandler={handleLocationSelectorSubmit}
                  coveoAutocompleteFields={coveoAutocompleteFields}
                />
              </div>
            )}
            <div className="nearby-communities-row-headless__inner">
              <CommunitiesSlider
                featuredItems={featuredItemsResults}
                displayedResults={displayedResults}
                isNearby={true}
                showPagination={showPagination}
                featuredItemsCta={featuredItemsCta}
              />
              {ctaNearbyLink && ctaNearbyLinkLabel && (
                <div className="nearby-community-row-headless__cta">
                  <a
                    href={ctaNearbyLink}
                    className="link link--small"
                    target=""
                  >
                    <span className="link__label">{ctaNearbyLinkLabel}</span>
                    <svg viewBox="0 0 18 18" width="18" role="presentation">
                      <use xlinkHref="#arrow-right"></use>
                    </svg>
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </SearchContext.Provider>
    </>
  );
};

export default NearbyCommunitiesRow;
